import React, { useEffect, useState } from 'react'
import { withStyles } from '@material-ui/core/styles'
import {
  Typography,
  Button,
  CircularProgress,
  Box,
  Divider,
  IconButton,
} from '@material-ui/core'
import FolderIcon from '@material-ui/icons/FolderOpen'
import DescriptionIcon from '@material-ui/icons/Description'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import HomeIcon from '@material-ui/icons/Home'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import styles from './styles'

const GoogleDriveConnect = ({
  classes,
  googleDrive,
  onFolderSelected,
  onNext,
  selectedFiles = [],
  onSelectionChange,
}) => {
  const {
    connected,
    loading,
    loadingFiles,
    folders,
    files,
    selectedFolder,
    breadcrumb,
    error,
    checkStatus,
    connect,
    browseFolders,
    openFolder,
    navigateTo,
    selectFolder,
  } = googleDrive

  const [isAnimating, setIsAnimating] = useState(false)

  const handleNext = () => {
    setIsAnimating(true)
    setTimeout(() => {
      setIsAnimating(false)
      if (onNext) onNext()
    }, 350)
  }

  const toggleFile = (file) => {
    const isSelected = selectedFiles.some(f => f.id === file.id)
    if (isSelected) {
      onSelectionChange(selectedFiles.filter(f => f.id !== file.id))
    } else {
      onSelectionChange([...selectedFiles, file])
    }
  }

  useEffect(() => {
    checkStatus()
  }, [checkStatus])

  useEffect(() => {
    if (connected && folders === null && breadcrumb.length === 0) {
      browseFolders('root')
    }
  }, [connected, browseFolders]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedFolder && files && files.length > 0) {
      onFolderSelected(selectedFolder, files)
    }
  }, [selectedFolder, files, onFolderSelected])

  if (!connected) {
    return (
      <Box textAlign="center" py={4} className={classes.root}>
        <Box mb={3}>
          <Typography variant="h6" style={{ fontWeight: 700, color: '#0F172A' }}>
            Connect Google Drive
          </Typography>
          <Typography style={{ color: '#64748B', maxWidth: '400px', margin: '8px auto' }}>
            Securely connect your Google Drive to browse and select the PDF files you want to use for your learning path.
          </Typography>
        </Box>
        <Box display="flex" justifyContent="center" gridGap="12px">
          <Button
            variant="contained"
            color="primary"
            onClick={connect}
            disabled={loading}
            style={{
              textTransform: 'none',
              padding: '10px 24px',
              borderRadius: '10px',
              fontWeight: 600,
              boxShadow: 'none',
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Connect Google Drive'}
          </Button>
          <Button
            variant="outlined"
            onClick={checkStatus}
            disabled={loading}
            style={{
              textTransform: 'none',
              padding: '10px 24px',
              borderRadius: '10px',
              fontWeight: 600,
              borderColor: '#E2E8F0',
            }}
          >
            Refresh Status
          </Button>
        </Box>
        {error && (
          <Typography style={{ color: '#EF4444', fontSize: '14px', marginTop: '16px' }}>
            {error}
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.statusCard}>
        <Box display="flex" alignItems="flex-start">
          <CheckCircleIcon style={{ color: '#10B981', fontSize: '28px', marginRight: '20px', marginTop: '2px' }} />
          <div>
            <Typography style={{ fontWeight: 700, color: '#0F172A', fontSize: '16px', lineHeight: 1.2 }}>
              Drive Connected
            </Typography>
            <Typography style={{ color: '#64748B', fontSize: '14px', marginTop: '4px' }}>
              Your Google Drive is ready to use
            </Typography>
          </div>
        </Box>
        <Button
          size="small"
          onClick={googleDrive.disconnect}
          style={{ textTransform: 'none', color: '#64748B', fontSize: '12px', fontWeight: 700 }}
        >
          Disconnect
        </Button>
      </div>

      <div>
        <Typography style={{ fontWeight: 700, marginBottom: '12px', color: '#1E293B' }}>
          Browse Folders
        </Typography>
        <div className={classes.browserContainer}>
          <div className={classes.breadcrumb}>
            <div className={classes.breadcrumbItem} onClick={() => browseFolders('root')}>
              <HomeIcon fontSize="small" />
              My Drive
            </div>
            {breadcrumb.map((crumb, i) => (
              <React.Fragment key={crumb.id}>
                <ChevronRightIcon style={{ color: '#CBD5E1', fontSize: '18px' }} />
                <div
                  className={i < breadcrumb.length - 1 ? classes.breadcrumbItem : classes.breadcrumbCurrent}
                  onClick={() => i < breadcrumb.length - 1 && navigateTo(i)}
                >
                  {crumb.name}
                </div>
              </React.Fragment>
            ))}
          </div>

          <div className={classes.folderList}>
            {(loading || folders === null) && !selectedFolder ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress size={24} />
              </Box>
            ) : folders.length === 0 ? (
              <Box textAlign="center" p={4} color="#94A3B8">
                No subfolders found
              </Box>
            ) : (
              folders.map(folder => (
                <div key={folder.id} className={classes.folderItem}>
                  <FolderIcon className={classes.folderIcon} />
                  <span className={classes.fileName}>{folder.name}</span>
                  <Box display="flex" gridGap="8px">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => selectFolder(folder.id, folder.name)}
                      style={{ textTransform: 'none', fontSize: '12px', borderRadius: '6px', borderColor: '#E2E8F0' }}
                    >
                      Select
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => openFolder(folder.id, folder.name)}
                      style={{
                        textTransform: 'none',
                        fontSize: '12px',
                        borderRadius: '6px',
                        backgroundColor: '#F1F5F9',
                        color: '#475569',
                        boxShadow: 'none',
                      }}
                    >
                      Open
                    </Button>
                  </Box>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {selectedFolder && (
        <div className={classes.selectionInfo}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography style={{ fontWeight: 700, color: '#0C4A6E', fontSize: '15px' }}>
              Selected Folder: {selectedFolder.name}
            </Typography>
            {files.length > 0 && !loadingFiles && (
              <IconButton
                className={`${classes.continueButton} ${isAnimating ? classes.animating : ''}`}
                onClick={handleNext}
                disabled={selectedFiles.length === 0}
              >
                <ArrowForwardIcon />
              </IconButton>
            )}
          </Box>

          {loadingFiles ? (
            <Box display="flex" alignItems="center">
              <CircularProgress size={16} style={{ marginRight: '12px' }} />
              <Typography style={{ fontSize: '14px', color: '#0369A1' }}>Scanning for PDFs...</Typography>
            </Box>
          ) : files.length === 0 ? (
            <Typography style={{ fontSize: '14px', color: '#0369A1' }}>No PDF files found in this folder.</Typography>
          ) : (
            <div>
              <Typography style={{ fontSize: '13px', fontWeight: 600, color: '#0369A1', marginBottom: '8px' }}>
                {selectedFiles.length} of {files.length} PDFs selected to process:
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {files.map(f => {
                  const isSelected = selectedFiles.some(sf => sf.id === f.id)
                  return (
                    <div
                      key={f.id}
                      onClick={() => toggleFile(f)}
                      className={classes.fileChip}
                      style={{
                        backgroundColor: isSelected ? '#3B82F6' : '#F1F5F9',
                        color: isSelected ? '#FFFFFF' : '#475569',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        border: isSelected ? '1px solid #2563EB' : '1px solid #E2E8F0',
                      }}
                    >
                      <DescriptionIcon style={{ fontSize: '14px', color: isSelected ? '#FFFFFF' : '#3B82F6' }} />
                      {f.name}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default withStyles(styles)(GoogleDriveConnect)
